import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { MessageService } from 'primeng/api';
import { StorageService } from 'src/app/services/storage.service';
import { UserDashboardService } from 'src/app/services/user-dashboard.service';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';

interface City {
  name: string;
  code: string;
}

export function noSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.value && typeof control.value === 'string') {
      if (control.value.trim().length === 0) {
        return { noSpaces: true };
      }
    }
    return null;
  };
}

@Component({
    selector: 'app-profile-form',
    templateUrl: './profile-form.component.html',
    styleUrls: ['./profile-form.component.scss'],
    standalone: false
})
export class ProfileFormComponent implements OnInit {
  firstName: any;
  lastName: any;
  email: any;
  organizationType: any;
  organizationName: any;
  phoneNumber: any;
  contactNumber: any;
  businessEmail: any;
  GST: any;
  PAN: any;
  CIN: any;
  address: string;
  billingType: any = '';

  // Control Variables
  userDetail: any;
  selectedCity!: City;
  email_notification_consent: boolean;
  reference_id_consent: any;
  userRole: string;
  order_reviewer: any;
  otp_validation: any;
  defaultCustomCheck: boolean;
  firstPartyName: any;
  estampFieldsData: any = [];
  esignFieldsData: any = [];
  stateData: any;
  branchData: any;
  articles: any;
  selectedState: any;
  selectedStateCode: any;
  account_id: string;
  estamp_default_fields: any;
  esign_default_fields: any;
  selectedArticleCode: any;
  displaySigningPositions: {
    display_name: string;
    sign_position: string;
    id: number;
    disabled: boolean;
  }[];
  submitted: boolean = false;
  profileDetails: FormGroup;
  public phonePattern: string = '[0-9]{10}';
  estamp_default: any;
  esign_default: any;
  esign_webcam: any;
  pageReloaded: boolean = false;
  organizationData: any;
  editMode: boolean = false;
  roles: any = [];
  showEditMode: boolean = false;

  constructor(
    private userService: UserProfileService,
    private messageService: MessageService,
    private storageService: StorageService,
    private fb: FormBuilder,
    private formBuilder: FormBuilder,
    private userDashboardService: UserDashboardService,
    private router: Router
  ) {
    this.estampFieldsData = [
      {
        label: 'First Party Name',
        default: false,
        custom: '',
        value: 'firstPartyName',
        type: 'text',
      },
      {
        label: 'Second Party Name',
        default: false,
        custom: '',
        value: 'secondPartyName',
        type: 'text',
      },
      {
        label: 'Stamp Duty Paid By',
        default: false,
        custom: '',
        value: 'stampDutyPaidBy',
        type: 'dropdown',
        options: [
          { name: 'First Party', code: 'first_party_name' },
          { name: 'Second Party', code: 'second_party_name' },
        ],
        optionValue: 'code',
        optionLabel: 'name',
      },
      {
        label: 'Purpose Of Stamp Duty',
        default: false,
        custom: '',
        value: 'purposeOfStampDuty',
        type: 'text',
      },
      {
        label: 'State',
        default: false,
        custom: '',
        value: 'state',
        type: 'dropdown',
        options: this.stateData,
        optionValue: 'id',
        optionLabel: 'state_name',
      },
      {
        label: 'Branch',
        default: false,
        custom: '',
        value: 'branch',
        type: 'dropdown',
        options: this.branchData,
        optionValue: 'id',
        optionLabel: 'name',
      },
      {
        label: 'Article Number',
        default: false,
        custom: '',
        value: 'articleNum',
        type: 'dropdown',
        options: this.articles,
        optionValue: 'article_code',
        optionLabel: 'article_code',
      },
    ];

    this.displaySigningPositions = [
      {
        display_name: 'Top Left',
        sign_position: 'TOP_LEFT',
        id: 1,
        disabled: false,
      },
      {
        display_name: 'Top Centre',
        sign_position: 'TOP_CENTRE',
        id: 2,
        disabled: false,
      },
      {
        display_name: 'Top Right',
        sign_position: 'TOP_RIGHT',
        id: 3,
        disabled: false,
      },
      {
        display_name: 'Middle Left',
        sign_position: 'MIDDLE_LEFT',
        id: 4,
        disabled: false,
      },
      {
        display_name: 'Middle Right',
        sign_position: 'MIDDLE_RIGHT',
        id: 5,
        disabled: false,
      },
      {
        display_name: 'Bottom Left',
        sign_position: 'BOTTOM_LEFT',
        id: 6,
        disabled: false,
      },
      {
        display_name: 'Bottom Centre',
        sign_position: 'BOTTOM_CENTRE',
        id: 7,
        disabled: false,
      },
      {
        display_name: 'Bottom Right',
        sign_position: 'BOTTOM_RIGHT',
        id: 8,
        disabled: false,
      },
    ];

    this.esignFieldsData = [
      {
        label: 'Party Name',
        default: false,
        custom: '',
        value: 'partyName',
        type: 'text',
      },
      {
        label: 'Party Email',
        default: false,
        custom: '',
        value: 'partyEmail',
        type: 'email',
      },
      {
        label: 'Sign Method',
        default: false,
        custom: '',
        value: 'signMethod',
        type: 'dropdown',
        options: [
          { name: 'Aadhaar', sign_method: 'AADHAAR', inactive: false },
          { name: 'Electronic', sign_method: 'ELECTRONIC', inactive: false },
          { name: 'DSC', sign_method: 'DSC', inactive: false },
        ],
        optionValue: 'sign_method',
        optionLabel: 'name',
      },
      {
        label: 'Contact Number',
        default: false,
        custom: '',
        value: 'contactNumber',
        type: 'text',
      },
    ];

    this.account_id = this.storageService.getAccountId() || null;

    this.profileDetails = this.formBuilder.group({
      firstName: [
        '',
        [Validators.required, noSpacesValidator(), Validators.maxLength(50)],
      ],
      lastName: [
        '',
        [Validators.required, noSpacesValidator(), Validators.maxLength(50)],
      ],
      contact: [
        '',
        [
          Validators.required,
          Validators.maxLength(10),
          Validators.minLength(10),
        ],
      ],
      email: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.userRole = this.storageService.getRole() || '';
    this.getUserDetail();
    this.reference_id_consent = this.storageService.getRefIdConsent() || null;
  }

  gotoDashboard() {
    this.userRole === 'CLIENT'
      ? this.router.navigate(['doqfy/dashboard'])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  // ==================== SECURITY VALIDATION METHODS ====================

  // Sanitize First Name and Last Name - Only letters, numbers, spaces
  sanitizeName(event: KeyboardEvent): boolean {
    const key = event.key;

    if (
      ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)
    ) {
      return true;
    }

    const allowedPattern = /^[a-zA-Z0-9\s]$/;
    if (!allowedPattern.test(key)) {
      event.preventDefault();
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Input',
        detail: 'Only letters, numbers, and spaces allowed',
        life: 2000,
      });
      return false;
    }
    return true;
  }

  // Sanitize Organization Name
  sanitizeOrganizationName(event: KeyboardEvent): boolean {
    const key = event.key;

    if (
      ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)
    ) {
      return true;
    }

    const allowedPattern = /^[a-zA-Z0-9\s\-&.,()\/]$/;
    if (!allowedPattern.test(key)) {
      event.preventDefault();
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Character',
        detail: 'Only alphanumeric and -&.,()/ allowed',
        life: 2000,
      });
      return false;
    }
    return true;
  }

  // Sanitize Phone Number
  sanitizePhoneNumber(event: KeyboardEvent): boolean {
    const key = event.key;
    const currentValue = (event.target as HTMLInputElement).value;

    if (
      ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)
    ) {
      return true;
    }

    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Input',
        detail: 'Only numbers allowed',
        life: 2000,
      });
      return false;
    }

    if (currentValue.length >= 10) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  // Sanitize GST
  sanitizeGST(event: KeyboardEvent): boolean {
    const key = event.key.toUpperCase();

    if (
      ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(
        event.key
      )
    ) {
      return true;
    }

    if (!/^[A-Z0-9]$/.test(key)) {
      event.preventDefault();
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Input',
        detail: 'Only letters and numbers allowed',
        life: 2000,
      });
      return false;
    }
    return true;
  }

  // Auto-convert GST to uppercase
  onGSTInput(event: any): void {
    this.GST = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  // Sanitize PAN
  sanitizePAN(event: KeyboardEvent): boolean {
    const key = event.key.toUpperCase();

    if (
      ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(
        event.key
      )
    ) {
      return true;
    }

    if (!/^[A-Z0-9]$/.test(key)) {
      event.preventDefault();
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Input',
        detail: 'Only letters and numbers allowed',
        life: 2000,
      });
      return false;
    }
    return true;
  }

  // Auto-convert PAN to uppercase
  onPANInput(event: any): void {
    this.PAN = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  // Sanitize Address
  sanitizeAddress(event: KeyboardEvent): boolean {
    const key = event.key;

    if (
      [
        'Backspace',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Delete',
        'Enter',
      ].includes(key)
    ) {
      return true;
    }

    const allowedPattern = /^[a-zA-Z0-9\s\-,./()#]$/;
    if (!allowedPattern.test(key)) {
      event.preventDefault();
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Character',
        detail: 'Special characters not allowed',
        life: 2000,
      });
      return false;
    }
    return true;
  }

  // Enhanced Paste Handler with field-specific sanitization
  handlePasteEventSecure(event: ClipboardEvent, fieldType: string): void {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') || '';
    let sanitized = '';

    switch (fieldType) {
      case 'firstName':
      case 'lastName':
        sanitized = pastedText.replace(/[^a-zA-Z0-9\s]/g, '').substring(0, 50);
        if (fieldType === 'firstName') {
          this.firstName = sanitized;
        } else {
          this.lastName = sanitized;
        }
        break;

      case 'organizationName':
        sanitized = pastedText
          .replace(/[^a-zA-Z0-9\s\-&.,()\/]/g, '')
          .substring(0, 50);
        this.organizationName = sanitized;
        break;

      case 'phone':
      case 'contactNumber':
        sanitized = pastedText.replace(/\D/g, '').substring(0, 10);
        if (fieldType === 'phone') {
          this.phoneNumber = sanitized;
        } else {
          this.contactNumber = sanitized;
        }
        break;

      case 'GST':
        sanitized = pastedText
          .replace(/[^A-Z0-9]/gi, '')
          .toUpperCase()
          .substring(0, 15);
        this.GST = sanitized;
        break;

      case 'PAN':
        sanitized = pastedText
          .replace(/[^A-Z0-9]/gi, '')
          .toUpperCase()
          .substring(0, 10);
        this.PAN = sanitized;
        break;

      case 'address':
        sanitized = pastedText
          .replace(/[^a-zA-Z0-9\s\-,./()#]/g, '')
          .substring(0, 200);
        this.address = sanitized;
        break;

      default:
        sanitized = pastedText;
    }

    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    target.value = sanitized;
    target.dispatchEvent(new Event('input'));

    if (sanitized !== pastedText) {
      this.messageService.add({
        severity: 'info',
        summary: 'Content Sanitized',
        detail: 'Invalid characters were removed from pasted content',
        life: 3000,
      });
    }
  }

  // Number Only For Contact (Enhanced)
  numberOnlyForContact(event: KeyboardEvent): boolean {
    const key = event.key;
    const currentValue = (event.target as HTMLInputElement).value;

    if (
      ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)
    ) {
      return true;
    }

    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
      return false;
    }

    if (currentValue.length >= 10) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  // ==================== SAVE METHODS WITH VALIDATION ====================

  savePersonalDetails() {
    this.submitted = true;

    if (this.profileDetails.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill all required fields correctly',
      });
      return;
    }

    // Sanitize inputs before submission
    this.firstName = this.firstName.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    this.lastName = this.lastName.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    this.contactNumber = this.contactNumber.replace(/\D/g, '');

    // Validate First Name
    if (this.firstName.length < 2 || this.firstName.length > 50) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid First Name',
        detail: 'First name must be 2-50 characters',
      });
      this.submitted = false;
      return;
    }

    // Validate Last Name
    if (this.lastName.length < 2 || this.lastName.length > 50) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Last Name',
        detail: 'Last name must be 2-50 characters',
      });
      this.submitted = false;
      return;
    }

    // Validate Contact Number
    const phonePattern = /^[6-9][0-9]{9}$/;
    if (!phonePattern.test(this.contactNumber)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Phone Number',
        detail: 'Phone must be 10 digits starting with 6-9',
      });
      this.submitted = false;
      return;
    }

    const data = {
      first_name: this.firstName,
      last_name: this.lastName,
      contact_number: this.contactNumber,
    };

    this.userService.editUserDetail(data).subscribe({
      next: (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Details updated successfully',
        });
        this.pageReloaded = true;
        this.getUserDetail();
        this.submitted = false;
      },
      error: (error: any) => {
        console.error('API Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: error.error?.message || 'Unable to update details',
        });
        this.submitted = false;
      },
    });
  }

  SaveOrganizationData() {
    // Validate all required fields
    if (
      !this.organizationName ||
      !this.phoneNumber ||
      !this.address ||
      !this.PAN ||
      !this.GST
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill all required fields',
      });
      return;
    }

    // Sanitize inputs before validation
    this.organizationName = this.organizationName
      .replace(/[^a-zA-Z0-9\s\-&.,()\/]/g, '')
      .trim();
    this.phoneNumber = this.phoneNumber.replace(/\D/g, '');
    this.GST = this.GST.replace(/[^A-Z0-9]/g, '').toUpperCase();
    this.PAN = this.PAN.replace(/[^A-Z0-9]/g, '').toUpperCase();
    this.address = this.address.replace(/[^a-zA-Z0-9\s\-,./()#]/g, '').trim();

    // Validate Organization Name
    if (this.organizationName.length < 2 || this.organizationName.length > 50) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Organization Name',
        detail: 'Organization name must be between 2-50 characters',
      });
      return;
    }

    // Validate Phone Number
    const phonePattern = /^[6-9][0-9]{9}$/;
    if (!phonePattern.test(this.phoneNumber)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Phone Number',
        detail: 'Phone must be 10 digits starting with 6-9',
      });
      return;
    }

    // Validate PAN
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!panPattern.test(this.PAN)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid PAN',
        detail:
          'PAN must be in format: ABCDE1234F (5 letters, 4 digits, 1 letter)',
      });
      return;
    }

    // Validate GST
    if (this.GST.length !== 15) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid GST',
        detail: 'GST number must be exactly 15 characters',
      });
      return;
    }

    // Validate Address
    if (this.address.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Address',
        detail: 'Address cannot be empty',
      });
      return;
    }

    const data = {
      organisation_name: this.organizationName,
      contact_number: this.phoneNumber,
      address: this.address,
      state_id: this.organizationData.state,
      city: this.organizationData.city,
      organisation_type: this.organizationData.organisation_type,
      pan: this.PAN,
      gst: this.GST,
      cin: this.CIN,
      code: this.organizationData.code,
    };

    this.userDashboardService.editOrganizationData(data).subscribe({
      next: (res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res.message || 'Organization data updated successfully',
        });
        this.cancelEdit();
      },
      error: (err) => {
        console.error('API Error:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: err.error?.message || 'Failed to update organization data',
        });
      },
    });
  }

  // ==================== OTHER METHODS ====================

  cancelEdit() {
    this.getUserDetail();
    this.editMode = false;
    this.showEditMode = true;
  }

  editOrganization() {
    this.editMode = true;
    this.showEditMode = false;
  }

  getOrganizationData() {
    this.userDashboardService.getOrganizationDetails().subscribe((res) => {
      this.organizationData = res['content'];
      this.organizationName = this.organizationData.name;
      this.phoneNumber = this.organizationData.contact_number;
      this.businessEmail = this.organizationData.email;
      this.GST = this.organizationData.gst ? this.organizationData.gst : 'N/A';
      this.PAN = this.organizationData.pan ? this.organizationData.pan : 'N/A';
      this.CIN = this.organizationData.cin ? this.organizationData.cin : 'N/A';
      this.address = this.organizationData.address;
    });
  }

  getUserDetail() {
    this.userService.getUserDetail().subscribe((res: any) => {
      this.userDetail = res['content'];
      this.firstName = this.userDetail.first_name;
      this.lastName = this.userDetail.last_name;
      this.storageService.setUser(this.firstName + ' ' + this.lastName);
      if (this.pageReloaded) {
        location.reload();
        this.pageReloaded = false;
      }
      this.email = this.userDetail.email;
      this.contactNumber = this.userDetail.contact_number;
      this.organizationType =
        this.userDetail.orgnisation_info.organisation_type;
      this.billingType = this.userDetail.orgnisation_info.pricing.billing || '';
      this.email_notification_consent =
        this.userDetail.email_notification_consent;
      this.roles = this.userDetail.roles;
      this.showEditMode = this.roles.includes('ORGANISATION-ADMIN');

      this.profileDetails.patchValue({
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        contact: this.contactNumber,
      });
    });
    this.getOrganizationData();
  }

  // ==================== EXISTING UTILITY METHODS ====================

  setAccountSettings() {
    this.userService.getOrderReviewConsent(this.account_id).subscribe((res) => {
      this.order_reviewer = res['content'].maker_checker;
      this.otp_validation = res['content'].esign_otp;
      this.esign_webcam = res['content'].esign_webcam;
      this.storageService.setOrderReviewConsent(this.order_reviewer);
      this.storageService.setOtpValidation(this.otp_validation);
      this.storageService.setSignatoryImageCapture(this.esign_webcam);

      this.estamp_default = res['content'].estamp_default;
      this.storageService.setEstampDefault(this.estamp_default);
      this.esign_default = res['content'].esign_default;
      this.storageService.setEsignDefault(this.esign_default);
      const stampSign =
        res['content']?.additional_configuration?.stamp_sign ??
        res['content']?.stamp_sign ??
        false;
      this.storageService.setStampSignatory(stampSign);
    });
  }

  numberOnly(event: any) {
    const pattern = /[0-9\-]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (
      (event.keyCode != 8 && !pattern.test(inputChar)) ||
      event.keyCode == 45
    ) {
      event.preventDefault();
    } else if (event.target.value === '0') {
      event.preventDefault();
    }
  }

  isEmailValid(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailPattern.test(email);
  }

  isOnlySpaces(input: string): boolean {
    return input.trim() === '';
  }

  noLeadingSpacesValidator(value) {
    return value && /^\s/.test(value);
  }

  notOnlyNumbersValidator(value) {
    return value && (/^\d+$/.test(value) || /^\d+\s+$/.test(value));
  }

  getDefaultFieldData(account_id) {
    this.userService.getDefaultFieldData(account_id).subscribe((res: any) => {
      this.estamp_default_fields = res['content']['estamp_default_fields'];
      this.estampFieldsData[0].custom =
        this.estamp_default_fields.first_party_name;
      this.estampFieldsData[1].custom =
        this.estamp_default_fields.second_party_name;
      this.estampFieldsData[2].custom =
        this.estamp_default_fields.stamp_duty_paid_by;
      this.estampFieldsData[3].custom = this.estamp_default_fields.description;
      this.getStateDetails();

      this.esign_default_fields = res['content']['esign_default_fields'];
      this.esignFieldsData[0].custom = this.esign_default_fields.party_name;
      this.esignFieldsData[1].custom = this.esign_default_fields.party_email;
      this.esignFieldsData[2].custom = this.esign_default_fields.sign_method;
      this.esignFieldsData[3].custom = this.esign_default_fields.contact_number;
    });
  }

  getStateDetails() {
    this.userDashboardService.getStates().subscribe((res: any) => {
      this.stateData = res['content'];
      this.estampFieldsData[4].options = this.stateData;

      if (
        this.estamp_default_fields.state_id &&
        this.estamp_default_fields.state_id !== '' &&
        this.estamp_default_fields.state_id !== null
      ) {
        this.estampFieldsData[4].custom = this.estamp_default_fields.state_id;
        this.getBranchDetails(this.estamp_default_fields.state_id);
      } else {
        this.estampFieldsData[4].custom = '';
      }

      this.stateData.forEach((state) => {
        if (
          this.estamp_default_fields &&
          state.id === this.estamp_default_fields.state_id
        ) {
          this.selectedStateCode = state.state_code;
          this.getArticles(this.selectedStateCode);
        }
      });
    });
  }

  onEstampChange(evt, field) {
    if (field.label === 'Stamp Duty Paid By') {
      this.estampFieldsData[2].custom = evt.value;
    }
    if (field.label === 'State') {
      this.selectedState = evt.value;
      this.getBranchDetails(this.selectedState);
      this.stateData.forEach((state) => {
        if (state.id === evt.value) {
          this.selectedStateCode = state.state_code;
          this.getArticles(this.selectedStateCode);
        }
      });
    }
    if (field.label === 'Branch') {
      this.estampFieldsData[5].custom = evt.value;
    }
    if (field.label === 'Article Number') {
      this.estampFieldsData[6].custom = evt.value;
    }
  }

  onEsignChange(evt, field) {
    if (field.label === 'Sign Method')
      this.esignFieldsData[2].custom = evt.value;
  }

  getBranchDetails(id: number) {
    const account_id = this.storageService.getAccountId() || null;
    this.userDashboardService
      .getBranchListbyStateId(id, account_id)
      .subscribe((res: any) => {
        this.branchData = res['content'];
        this.branchData = this.branchData.filter(
          (element) => element.is_active === true
        );
        this.estampFieldsData[5].options = this.branchData;

        this.estamp_default_fields
          ? (this.estampFieldsData[5].custom =
              this.estamp_default_fields.branch_id)
          : (this.estampFieldsData[5].custom = '');
      });
  }

  getArticles(state_code) {
    this.userService.getArticles(state_code).subscribe((resp: any) => {
      if (resp.status === 200) {
        this.articles = resp['content'];
        this.estampFieldsData[6].options = this.articles;

        this.estamp_default_fields
          ? (this.estampFieldsData[6].custom =
              this.estamp_default_fields.article_code)
          : (this.estampFieldsData[6].custom = '');
      }
    });
  }

  toggleNotifyStatus() {
    let reqParam = {
      notification_type: 'EMAIL',
      category: 'change_order_status',
    };
    this.userService.notificationConsent(reqParam).subscribe((res: any) => {
      this.email_notification_consent = !this.email_notification_consent;
      this.messageService.add({ severity: 'success', detail: res.message });
    });
  }

  getRefIdConsent() {
    this.userService
      .getRefIdConsent('reference_number')
      .subscribe((res: any) => {
        this.storageService.setRefIdConsent(res.content.consent);
        this.reference_id_consent = res.content.consent;
      });
  }

  toggleRefNumStatus() {
    let reqParam = {
      consent_type: 'reference_number',
    };
    this.userService.refIdConsent(reqParam).subscribe((res: any) => {
      this.reference_id_consent = !this.reference_id_consent;
      this.messageService.add({ severity: 'success', detail: res.message });
      this.getRefIdConsent();
    });
  }

  toggleOrderReviewer() {
    this.order_reviewer = !this.order_reviewer;
    let review = '';
    if (this.order_reviewer) {
      review = 'on';
    } else {
      review = 'off';
    }
    let prams = { account_id: this.account_id, maker_checker: review };
    this.userService.orderReviewConsent(prams).subscribe((res) => {
      if (review === 'on') {
        this.messageService.add({
          severity: 'success',
          summary: 'Enabled Checker option upon place order!',
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: 'Disabled Checker option upon place order!',
        });
      }
      this.setOrderReviewer();
    });
  }

  setOrderReviewer() {
    this.userService.getOrderReviewConsent(this.account_id).subscribe((res) => {
      this.order_reviewer = res['content'].maker_checker;
      this.storageService.setOrderReviewConsent(this.order_reviewer);
    });
  }

  toggleOtpValidation() {
    this.otp_validation = !this.otp_validation;
    let esignOtp;
    if (this.otp_validation) {
      esignOtp = 'on';
    } else {
      esignOtp = 'off';
    }
    let prams = { account_id: this.account_id, esign_otp: esignOtp };
    this.userService.orderReviewConsent(prams).subscribe((res) => {
      this.setSignOtp();
    });
  }

  setSignOtp() {
    this.userService.getOrderReviewConsent(this.account_id).subscribe((res) => {
      this.otp_validation = res['content'].esign_otp;
      this.storageService.setOtpValidation(this.otp_validation);
    });
  }

  toggleDefaultCustom(defaultToggle) {
    defaultToggle = !defaultToggle;
  }

  postDefaultEstampData() {
    this.submitted = true;

    if (
      this.estampFieldsData[0].default === true &&
      this.isOnlySpaces(this.estampFieldsData[0].custom)
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'First Party Name should not contain only spaces',
      });
      return;
    }

    if (
      this.estampFieldsData[0].default === true &&
      this.noLeadingSpacesValidator(this.estampFieldsData[0].custom)
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'First Party Name cannot start with spaces',
      });
      return;
    }

    if (
      this.estampFieldsData[0].default === true &&
      this.notOnlyNumbersValidator(this.estampFieldsData[0].custom)
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'First Party Name cannot contains only numbers',
      });
      return;
    }

    if (
      this.estampFieldsData[1].default === true &&
      this.isOnlySpaces(this.estampFieldsData[1].custom)
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Second Party Name should not contain only spaces',
      });
      return;
    }

    if (
      this.estampFieldsData[1].default === true &&
      this.noLeadingSpacesValidator(this.estampFieldsData[1].custom)
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Second Party Name cannot start with spaces',
      });
      return;
    }

    if (
      this.estampFieldsData[1].default === true &&
      this.notOnlyNumbersValidator(this.estampFieldsData[1].custom)
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Second Party Name cannot contains only numbers',
      });
      return;
    }

    let payload = {
      account_id: this.account_id,
      estamp_default_fields: {
        first_party_name: this.estampFieldsData[0].custom,
        second_party_name: this.estampFieldsData[1].custom,
        stamp_duty_paid_by: this.estampFieldsData[2].custom,
        description: this.estampFieldsData[3].custom,
        execution_date: null,
        state_code: this.selectedStateCode,
        branch_id: this.estampFieldsData[5].custom,
        article_code: this.estampFieldsData[6].custom,
      },
    };

    this.userService.postDefaultFieldData(payload).subscribe((res: any) => {
      this.messageService.add({ severity: 'success', detail: res.message });
      this.resetDefaultSettings();
      this.submitted = false;
      this.getDefaultFieldData(this.account_id);
    });
  }

  postDefaultEsignData() {
    this.submitted = true;

    if (
      this.esignFieldsData[0].custom.length < 2 &&
      this.esignFieldsData[0].default === true
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Party Name cannot be blank',
      });
      return;
    }

    if (
      this.esignFieldsData[3].custom.length < 10 &&
      this.esignFieldsData[3].default === true
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Contact Number should be of 10 digits',
      });
      return;
    }

    if (
      this.esignFieldsData[1].default === true &&
      !this.isEmailValid(this.esignFieldsData[1].custom)
    ) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Email is not valid',
      });
      return;
    }

    let payload = {
      account_id: this.account_id,
      esign_default_fields: {
        party_name: this.esignFieldsData[0].custom,
        party_email: this.esignFieldsData[1].custom,
        sign_method: this.esignFieldsData[2].custom,
        contact_number: this.esignFieldsData[3].custom,
      },
    };

    this.userService.postDefaultFieldData(payload).subscribe(
      (res: any) => {
        this.messageService.add({ severity: 'success', detail: res.message });
        this.resetDefaultSettings();
        this.submitted = false;
        this.getDefaultFieldData(this.account_id);
      },
      (error) => {}
    );
  }

  resetDefaultSettings() {
    this.estampFieldsData.forEach((field) => {
      field.default = false;
    });
    this.esignFieldsData.forEach((field) => {
      field.default = false;
    });
  }

  enterNumberOnly(event: any) {
    const pattern = /[0-9\-]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (
      (event.keyCode != 8 && !pattern.test(inputChar)) ||
      event.keyCode == 45
    ) {
      event.preventDefault();
    } else if (event.target.value === '0') {
      event.preventDefault();
    }
  }

  toggleDefaultEstamp() {
    this.estamp_default = !this.estamp_default;
    let estamp_default;
    if (this.estamp_default) {
      estamp_default = 'on';
    } else {
      estamp_default = 'off';
    }
    let prams = { account_id: this.account_id, estamp_default: estamp_default };
    this.userService.orderReviewConsent(prams).subscribe((res) => {
      this.setEstampDefault();
    });
  }

  setEstampDefault() {
    this.userService.getOrderReviewConsent(this.account_id).subscribe((res) => {
      this.estamp_default = res['content'].estamp_default;
      this.storageService.setEstampDefault(this.estamp_default);
    });
  }

  toggleDefaultEsign() {
    this.esign_default = !this.esign_default;
    let esign_default;
    if (this.esign_default) {
      esign_default = 'on';
    } else {
      esign_default = 'off';
    }
    let prams = { account_id: this.account_id, esign_default: esign_default };
    this.userService.orderReviewConsent(prams).subscribe((res) => {
      this.setEsignDefault();
    });
  }

  setEsignDefault() {
    this.userService.getOrderReviewConsent(this.account_id).subscribe((res) => {
      this.esign_default = res['content'].esign_default;
      this.storageService.setEsignDefault(this.esign_default);
    });
  }

  toggleSignatoryImageCapture() {
    this.esign_webcam = !this.esign_webcam;
    let enableCapture;
    if (this.esign_webcam) {
      enableCapture = 'on';
    } else {
      enableCapture = 'off';
    }
    let prams = { account_id: this.account_id, esign_webcam: enableCapture };
    this.userService.orderReviewConsent(prams).subscribe((res) => {
      this.setSignatoryImageCapture();
    });
  }

  setSignatoryImageCapture() {
    this.userService.getOrderReviewConsent(this.account_id).subscribe((res) => {
      this.esign_webcam = res['content'].esign_webcam;
      this.storageService.setSignatoryImageCapture(this.esign_webcam);
    });
  }
}
